import { AppCard, AppCardBody, AppText, kindredFontFamilies } from "../../ui-foundation";
import {
  DocumentList,
  DocumentPageScaffold,
  DocumentSection,
  DocumentSubsection,
} from "@/components/DocumentScaffold";
import {
  KINDRED_BREACH_RESPONSE_PROCESS_BULLETS,
  KINDRED_BREACH_REPORTING_COPY,
  KINDRED_COUNTRY_ANNEX_DATA_RIGHTS_BULLETS,
  KINDRED_DATA_PROTECTION_ESCALATION_BULLETS,
  KINDRED_DATA_PROTECTION_ESCALATION_LEAD,
  KINDRED_DATA_RIGHTS_REGION_BULLETS,
  KINDRED_DATA_RIGHTS_SCOPE_COPY,
  KINDRED_FEEDBACK_CHANNEL_PATH,
  KINDRED_FORMAL_REQUEST_PROCESS_BULLETS,
  KINDRED_PRIVACY_REQUEST_HANDLING_BULLETS,
  KINDRED_PRIVACY_DATA_RIGHTS_LABEL,
  KINDRED_SUPPORT_EMAIL,
  STEADY_HOUSE_SUPPORT_URL,
} from "../../shared/support";

const DataRights = () => {
  return (
    <DocumentPageScaffold
      title={KINDRED_PRIVACY_DATA_RIGHTS_LABEL}
      updatedLabel="Last updated: April 2026"
      fallbackPath="/"
    >
      <DocumentSection title="1. Your rights">
        <AppText>{KINDRED_DATA_RIGHTS_SCOPE_COPY}</AppText>
        <DocumentList items={[...KINDRED_DATA_RIGHTS_REGION_BULLETS]} />
      </DocumentSection>

      <DocumentSection title="2. Rights you can act on inside Kindred">
        <DocumentSubsection title="2.1 Access and portability">
          <AppText>
            Use <strong>Menu &gt; Settings &gt; Delete account</strong> to download a JSON export of the
            personal data currently linked to your account.
          </AppText>
        </DocumentSubsection>

        <DocumentSubsection title="2.2 Erasure">
          <AppText>
            Use <strong>Menu &gt; Settings &gt; Delete account</strong> to request deletion. Once the request is
            approved, Kindred removes the linked account data and deletes the auth account.
          </AppText>
        </DocumentSubsection>

        <DocumentSubsection title="2.3 Correction">
          <AppText>
            Update the profile details you control inside the app. If something cannot be corrected there, ask
            support to help.
          </AppText>
        </DocumentSubsection>
      </DocumentSection>

      <DocumentSection title="3. Formal requests handled through support">
        <AppText>
          Use the{" "}
          <a
            href={STEADY_HOUSE_SUPPORT_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="kindred-inline-link"
          >
            Steady House support page
          </a>{" "}
          or email{" "}
          <a href={`mailto:${KINDRED_SUPPORT_EMAIL}`} className="kindred-inline-link">
            {KINDRED_SUPPORT_EMAIL}
          </a>{" "}
          if you need help with a formal privacy request, you cannot access your in-app controls, or you want
          a review of how Kindred handled your data.
        </AppText>
        <DocumentList items={[...KINDRED_FORMAL_REQUEST_PROCESS_BULLETS, ...KINDRED_PRIVACY_REQUEST_HANDLING_BULLETS]} />
      </DocumentSection>

      <DocumentSection title="4. Consent and objections">
        <AppText>
          Where Kindred relies on consent for an optional feature, you can withdraw that consent. Where
          Kindred relies on legitimate interests, you can object and ask us to review whether our reason for
          processing still overrides your rights and freedoms.
        </AppText>
      </DocumentSection>

      <DocumentSection title="5. Complaints, breaches, and escalation">
        <AppText>
          We would usually expect privacy and data-rights requests to come to us first so they can be logged,
          reviewed, and answered properly.
        </AppText>
        <AppText>{KINDRED_BREACH_REPORTING_COPY}</AppText>
        <DocumentList items={[...KINDRED_BREACH_RESPONSE_PROCESS_BULLETS]} />
        <AppText>{KINDRED_DATA_PROTECTION_ESCALATION_LEAD}</AppText>
        <DocumentList items={[...KINDRED_DATA_PROTECTION_ESCALATION_BULLETS]} />
      </DocumentSection>

      <DocumentSection title="6. Feedback versus formal rights requests">
        <AppText>
          For bugs, product ideas, or feature suggestions, signed-in members should use{" "}
          <strong>{KINDRED_FEEDBACK_CHANNEL_PATH}</strong>. That channel is for product feedback, not for formal
          privacy requests or complaints.
        </AppText>
        <AppText>
          Kindred keeps a clear split between everyday product feedback and formal privacy requests so both
          can be handled properly.
        </AppText>
      </DocumentSection>

      <DocumentSection title="7. Country Annexes">
        <DocumentList items={[...KINDRED_COUNTRY_ANNEX_DATA_RIGHTS_BULLETS]} />
      </DocumentSection>

      <AppCard backgroundColor="$panelBackground">
        <AppCardBody alignItems="center">
          <AppText
            textAlign="center"
            fontFamily={kindredFontFamilies.sansBold}
            fontSize="$md"
            lineHeight="$md"
          >
            Kindred keeps the main access, correction, and deletion controls in product, with human support
            available when a member needs help or a more formal review.
          </AppText>
        </AppCardBody>
      </AppCard>
    </DocumentPageScaffold>
  );
};

export default DataRights;
